const SUPPORTED_WIDGET_TYPES = new Set(["stat", "line", "area", "bar", "gauge", "table"]);
const SUPPORTED_WIDGET_SIZES = new Set(["sm", "md", "lg"]);
const NUMERIC_WIDGET_TYPES = new Set(["line", "area", "bar", "gauge"]);
const STATUS_TONES = {
  unknown: { label: "Unknown", tone: "#475569", bg: "#F1F5F9" },
  unconfigured: { label: "No threshold", tone: "#1D4ED8", bg: "#EFF6FF" },
  normal: { label: "Normal", tone: "#166534", bg: "#ECFDF3" },
  warning: { label: "Warning", tone: "#B45309", bg: "#FFF7ED" },
  critical: { label: "Critical", tone: "#B42318", bg: "#FEF2F2" },
};

const FORMULA_KEYWORDS = new Set([
  "and",
  "or",
  "not",
  "if",
  "else",
  "for",
  "in",
  "min",
  "max",
  "abs",
  "round",
  "pow",
  "int",
  "float",
  "len",
  "sum",
  "true",
  "false",
  "null",
]);

export const normalizeMetricKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

export const normalizeSchemaDefinition = (schema) => {
  if (!Array.isArray(schema)) return [];

  return schema
    .filter((item) => item && typeof item === "object")
    .filter((item) => item.key)
    .map((item) => {
      const type = String(item.type || "number").toLowerCase();
      return {
        ...item,
        key: String(item.key),
        normalizedKey: normalizeMetricKey(item.key),
        label: item.label || item.key,
        type,
      };
    });
};

export const buildSchemaIndex = (schema) => {
  return normalizeSchemaDefinition(schema).reduce((acc, field) => {
    acc[field.normalizedKey] = field;
    return acc;
  }, {});
};

const normalizeWidgetType = (value) => {
  const normalized = String(value || "stat").toLowerCase();
  return SUPPORTED_WIDGET_TYPES.has(normalized) ? normalized : "stat";
};

const normalizeWidgetSize = (value, widgetType) => {
  const normalized = String(value || (NUMERIC_WIDGET_TYPES.has(widgetType) ? "lg" : "md")).toLowerCase();
  return SUPPORTED_WIDGET_SIZES.has(normalized) ? normalized : "md";
};

export const resolveUnit = (field) => {
  if (!field) return "";
  if (field.unit === "Custom") return field.customUnit || "";
  return field.unit || "";
};

export const formatMetricValue = (value, unit = "") => {
  if (value === null || value === undefined || value === "") return "--";
  if (Number.isFinite(Number(value)) && String(value).trim() !== "") {
    const num = Number(value);
    const rounded = Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.00$/, "");
    return `${rounded}${unit ? ` ${unit}` : ""}`;
  }
  return `${value}${unit ? ` ${unit}` : ""}`;
};

export const buildDefaultDashboardConfig = (schema) => {
  const normalizedSchema = normalizeSchemaDefinition(schema);
  if (!normalizedSchema.length) return [];

  const visibleFields = normalizedSchema.filter((field) => field.visible !== false);
  const candidates = visibleFields.length ? visibleFields : normalizedSchema;

  const numericFields = candidates.filter((field) => field.type === "number" || field.type === "integer" || field.type === "float");
  const nonNumericFields = candidates.filter((field) => !numericFields.includes(field));

  const summaryFields = [...numericFields, ...nonNumericFields].slice(0, 4);
  const trendFields = numericFields.filter((field) => !summaryFields.includes(field)).slice(0, 4);

  const widgets = [];

  summaryFields.forEach((field, index) => {
    widgets.push({
      id: `default-summary-${index + 1}`,
      title: field.label,
      metric_key: field.key,
      widget_type: "stat",
      size: "sm",
      aggregation: "latest",
    });
  });

  trendFields.forEach((field, index) => {
    widgets.push({
      id: `default-trend-${index + 1}`,
      title: `${field.label} trend`,
      metric_key: field.key,
      widget_type: "line",
      size: "lg",
      aggregation: "avg",
    });
  });

  if (!widgets.length) {
    const first = candidates[0];
    widgets.push({
      id: "default-fallback",
      title: first.label,
      metric_key: first.key,
      widget_type: "stat",
      size: "md",
      aggregation: "latest",
    });
  }

  return widgets;
};

const extractFormulaVariables = (formula) => {
  const tokens = String(formula || "").match(/\b[A-Za-z_][A-Za-z0-9_]*\b/g) || [];
  return [...new Set(tokens)]
    .map((token) => token.trim())
    .filter(Boolean)
    .filter((token) => !FORMULA_KEYWORDS.has(token.toLowerCase()));
};

export const normalizeDashboardConfig = (dashboardConfig, schema) => {
  const schemaIndex = buildSchemaIndex(schema);
  const inputWidgets = Array.isArray(dashboardConfig) ? dashboardConfig : [];
  const errors = [];

  if (!inputWidgets.length) {
    return {
      widgets: buildDefaultDashboardConfig(schema),
      errors,
      usedDefault: true,
    };
  }

  const seenIds = new Set();
  const widgets = [];

  inputWidgets.forEach((widget, index) => {
    const prefix = `dashboard_config[${index + 1}]`;
    if (!widget || typeof widget !== "object") {
      errors.push(`${prefix}: widget must be an object.`);
      return;
    }

    const metricKeyRaw = widget.metric_key || widget.metricKey || widget.key;
    const metricKey = metricKeyRaw ? String(metricKeyRaw) : "";
    const normalizedMetric = normalizeMetricKey(metricKey);
    const formula = String(widget.formula || "").trim();

    if (!metricKey && !formula) {
      errors.push(`${prefix}: provide metric_key or formula.`);
      return;
    }

    const field = normalizedMetric ? schemaIndex[normalizedMetric] : null;
    if (metricKey && !field && !formula) {
      errors.push(`${prefix}: metric_key '${metricKey}' is not defined in schema.`);
      return;
    }

    const widgetType = normalizeWidgetType(widget.widget_type || widget.widgetType || widget.chart_type);
    const widgetSize = normalizeWidgetSize(widget.size, widgetType);

    if (NUMERIC_WIDGET_TYPES.has(widgetType) && field && field.type !== "number" && field.type !== "integer" && field.type !== "float" && !formula) {
      errors.push(`${prefix}: widget_type '${widgetType}' requires numeric metric.`);
      return;
    }

    const widgetId = String(widget.id || `${normalizedMetric || "widget"}-${index + 1}`);
    if (seenIds.has(widgetId)) {
      errors.push(`${prefix}: duplicate widget id '${widgetId}'.`);
      return;
    }
    seenIds.add(widgetId);

    if (formula) {
      const missingInputs = extractFormulaVariables(formula).filter(
        (key) => !schemaIndex[normalizeMetricKey(key)],
      );
      if (missingInputs.length) {
        errors.push(`${prefix}: formula references unknown inputs ${missingInputs.join(", ")}.`);
      }
    }

    widgets.push({
      ...widget,
      id: widgetId,
      title: widget.title || field?.label || metricKey || `Widget ${index + 1}`,
      metric_key: metricKey,
      metricKey: metricKey,
      widget_type: widgetType,
      widgetType,
      size: widgetSize,
      aggregation: String(widget.aggregation || "latest").toLowerCase(),
      unit: widget.unit || resolveUnit(field),
      formula,
    });
  });

  return {
    widgets,
    errors,
    usedDefault: false,
  };
};

const compareThreshold = (value, threshold, operator) => {
  if (!Number.isFinite(value) || !Number.isFinite(threshold)) return false;
  switch (operator) {
    case ">":
      return value > threshold;
    case ">=":
      return value >= threshold;
    case "<":
      return value < threshold;
    case "<=":
      return value <= threshold;
    case "==":
      return value === threshold;
    case "!=":
      return value !== threshold;
    default:
      return false;
  }
};

export const evaluateThresholdStatus = (rules, metricKey, rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return STATUS_TONES.unknown;
  }

  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) {
    return STATUS_TONES.unknown;
  }

  const rule = Array.isArray(rules)
    ? rules.find((item) => normalizeMetricKey(item?.metric_key) === normalizeMetricKey(metricKey))
    : null;

  if (!rule) {
    return STATUS_TONES.unconfigured;
  }

  const operator = String(rule.operator || ">").trim();
  const warning = Number(rule.warning_threshold);
  const critical = Number(rule.critical_threshold);

  if (Number.isFinite(critical) && compareThreshold(numericValue, critical, operator)) {
    return STATUS_TONES.critical;
  }

  if (Number.isFinite(warning) && compareThreshold(numericValue, warning, operator)) {
    return STATUS_TONES.warning;
  }

  if (!Number.isFinite(warning) && !Number.isFinite(critical)) {
    return STATUS_TONES.unconfigured;
  }

  return STATUS_TONES.normal;
};

export const resolveOperationalMetricStatus = (deviceStatus, metricStatus, hasTelemetry) => {
  if (!hasTelemetry) {
    return STATUS_TONES.unknown;
  }

  const state = String(deviceStatus?.status || "").toLowerCase();
  if (state === "online") {
    return metricStatus || STATUS_TONES.unknown;
  }

  if (state === "stale") {
    return {
      ...STATUS_TONES.unknown,
      label: "Unknown (stale data)",
    };
  }

  if (state === "offline") {
    return {
      ...STATUS_TONES.unknown,
      label: "Unknown (device offline)",
    };
  }

  if (state === "disabled") {
    return {
      ...STATUS_TONES.unknown,
      label: "Unknown (device disabled)",
    };
  }

  return STATUS_TONES.unknown;
};

export const buildChartSeries = (rows, metricKey, maxPoints = 24) => {
  if (!Array.isArray(rows) || !metricKey) return [];

  const points = rows
    .slice(0, Math.max(1, maxPoints))
    .reverse()
    .map((row) => {
      const rawValue = row?.[metricKey];
      const numericValue = Number(rawValue);
      if (!Number.isFinite(numericValue)) return null;

      const timestamp = row?.server_time || row?.timestamp || null;
      const label = timestamp ? String(timestamp).slice(11, 19) : "--";

      return {
        time: label,
        value: numericValue,
      };
    })
    .filter(Boolean);

  return points;
};

export const getTrendLabel = (chartData = []) => {
  if (!Array.isArray(chartData) || chartData.length < 2) return "No trend";

  const first = Number(chartData[0]?.value);
  const last = Number(chartData[chartData.length - 1]?.value);
  if (!Number.isFinite(first) || !Number.isFinite(last)) return "No trend";

  const delta = last - first;
  if (Math.abs(delta) < 0.001) return "Flat";
  return delta > 0 ? "Rising" : "Falling";
};

export const evaluateFormula = (formula, row) => {
  if (!formula || !row || typeof row !== "object") {
    return { value: null, missingInputs: [] };
  }

  const variables = extractFormulaVariables(formula);
  const context = {};
  const missingInputs = [];

  variables.forEach((variable) => {
    const raw = row[variable];
    const numeric = Number(raw);
    if (raw === null || raw === undefined || raw === "" || !Number.isFinite(numeric)) {
      missingInputs.push(variable);
      return;
    }

    context[variable] = numeric;
  });

  if (missingInputs.length) {
    return { value: null, missingInputs };
  }

  try {
    const keys = Object.keys(context);
    const values = Object.values(context);
    // eslint-disable-next-line no-new-func
    const fn = new Function(...keys, `return (${formula});`);
    const value = fn(...values);

    if (!Number.isFinite(Number(value))) {
      return { value: null, missingInputs: [] };
    }

    return { value: Number(value), missingInputs: [] };
  } catch (error) {
    return { value: null, missingInputs: [] };
  }
};

export const evaluateDerivedRule = (rule, latestPoint) => {
  const name = rule?.name || rule?.target_key || "Derived KPI";
  const formula = String(rule?.formula || "").trim();
  const targetKey = rule?.target_key || "";

  if (!formula) {
    return {
      id: `derived-${targetKey || name}`,
      name,
      formula,
      targetKey,
      value: null,
      error: "Widget configuration is incomplete.",
    };
  }

  const evaluation = evaluateFormula(formula, latestPoint || {});
  if (evaluation.missingInputs.length) {
    return {
      id: `derived-${targetKey || name}`,
      name,
      formula,
      targetKey,
      value: null,
      error: "This KPI cannot be calculated because required inputs are unavailable.",
    };
  }

  if (evaluation.value === null) {
    return {
      id: `derived-${targetKey || name}`,
      name,
      formula,
      targetKey,
      value: null,
      error: "Invalid formula or unsupported expression.",
    };
  }

  return {
    id: `derived-${targetKey || name}`,
    name,
    formula,
    targetKey,
    value: evaluation.value,
    error: null,
  };
};



